/**
 * McDuffy Box QR — tiny self-contained QR encoder (byte mode, ECC level M,
 * versions 1-10, full mask-penalty selection). No external assets (label-print
 * pages must stay self-contained), no dependencies.
 *
 * Built for the Box Button (BOX-QR-DESIGN-2026-07-15.md): the welcome-card
 * print page reads &box=<token> and renders the /pages/box?t=<token> QR with
 * this. Unlike outputs/upgrade-insert (whose QR is a pre-generated static
 * path), the box token differs per subscription, so encoding happens at
 * print/render time.
 *
 * API (window.McDuffyBoxQR / module.exports):
 *   matrix(text)            -> { size, get(x, y) -> 0|1, modules: Uint8Array }
 *   svg(text, opts)         -> '<svg …>' string (crispEdges path, quiet zone 4)
 *       opts: { dark: '#0F356F', light: '#FFFFFF', ariaLabel: 'QR code' }
 *
 * Output verified against an independent decoder (jsQR) in
 * mcduffy-backend/scripts/test-box-hub.js's sibling scratch verification and
 * at build time — do not hand-edit the tables below.
 */
(function () {
  'use strict';

  // ---- GF(256) Reed-Solomon (QR polynomial 0x11D) --------------------------
  var GF_EXP = new Uint8Array(512);
  var GF_LOG = new Uint8Array(256);
  (function () {
    var x = 1;
    for (var i = 0; i < 255; i++) {
      GF_EXP[i] = x;
      GF_LOG[x] = i;
      x <<= 1;
      if (x & 0x100) x ^= 0x11D;
    }
    for (var j = 255; j < 512; j++) GF_EXP[j] = GF_EXP[j - 255];
  })();
  function gfMul(a, b) {
    if (a === 0 || b === 0) return 0;
    return GF_EXP[GF_LOG[a] + GF_LOG[b]];
  }
  function rsGeneratorPoly(degree) {
    var poly = [1];
    for (var d = 0; d < degree; d++) {
      var next = new Array(poly.length + 1).fill(0);
      for (var i = 0; i < poly.length; i++) {
        next[i] ^= gfMul(poly[i], GF_EXP[d]);
        next[i + 1] ^= poly[i];
      }
      poly = next;
    }
    return poly; // lowest-degree first: poly[0] = constant, poly[degree] = leading 1
  }
  function rsComputeEcc(data, degree) {
    var gen = rsGeneratorPoly(degree);
    var res = new Array(degree).fill(0);
    for (var i = 0; i < data.length; i++) {
      var factor = data[i] ^ res[0];
      res.shift();
      res.push(0);
      if (factor !== 0) {
        for (var j = 0; j < degree; j++) {
          // res[0] aligns with the power just below the leading term, so it
          // subtracts g_{degree-1} … res[degree-1] subtracts g_0 (constant).
          res[j] ^= gfMul(gen[degree - 1 - j], factor);
        }
      }
    }
    return res;
  }

  // ---- ECC level M block tables, versions 1-10 -----------------------------
  // [version] -> { ecPerBlock, blocks: [[count, dataCodewords], …] }
  var M_BLOCKS = {
    1:  { ec: 10, blocks: [[1, 16]] },
    2:  { ec: 16, blocks: [[1, 28]] },
    3:  { ec: 26, blocks: [[1, 44]] },
    4:  { ec: 18, blocks: [[2, 32]] },
    5:  { ec: 24, blocks: [[2, 43]] },
    6:  { ec: 16, blocks: [[4, 27]] },
    7:  { ec: 18, blocks: [[4, 31]] },
    8:  { ec: 22, blocks: [[2, 38], [2, 39]] },
    9:  { ec: 22, blocks: [[3, 36], [2, 37]] },
    10: { ec: 26, blocks: [[4, 43], [1, 44]] }
  };
  var ALIGNMENT = {
    1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
    6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50]
  };
  function totalDataCodewords(version) {
    var spec = M_BLOCKS[version];
    var total = 0;
    for (var i = 0; i < spec.blocks.length; i++) total += spec.blocks[i][0] * spec.blocks[i][1];
    return total;
  }

  // ---- Bit buffer -----------------------------------------------------------
  function BitBuffer() { this.bits = []; }
  BitBuffer.prototype.push = function (value, length) {
    for (var i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  };
  BitBuffer.prototype.toBytes = function () {
    var bytes = [];
    for (var i = 0; i < this.bits.length; i += 8) {
      var b = 0;
      for (var j = 0; j < 8; j++) b = (b << 1) | (this.bits[i + j] || 0);
      bytes.push(b);
    }
    return bytes;
  };

  function toUtf8Bytes(text) {
    var str = String(text == null ? '' : text);
    if (typeof TextEncoder !== 'undefined') return Array.prototype.slice.call(new TextEncoder().encode(str));
    var out = [];
    for (var i = 0; i < str.length; i++) {
      var c = str.charCodeAt(i);
      if (c < 0x80) out.push(c);
      else if (c < 0x800) { out.push(0xC0 | (c >> 6), 0x80 | (c & 0x3F)); }
      else { out.push(0xE0 | (c >> 12), 0x80 | ((c >> 6) & 0x3F), 0x80 | (c & 0x3F)); }
    }
    return out;
  }

  function pickVersion(byteLength) {
    for (var v = 1; v <= 10; v++) {
      var countBits = v <= 9 ? 8 : 16;
      var capacityBits = totalDataCodewords(v) * 8;
      if (4 + countBits + byteLength * 8 <= capacityBits) return v;
    }
    throw new Error('McDuffyBoxQR: text too long (max ~' + (totalDataCodewords(10) - 3) + ' bytes at ECC M)');
  }

  function buildCodewords(text) {
    var data = toUtf8Bytes(text);
    var version = pickVersion(data.length);
    var countBits = version <= 9 ? 8 : 16;
    var capacity = totalDataCodewords(version);

    var buf = new BitBuffer();
    buf.push(4, 4); // byte mode
    buf.push(data.length, countBits);
    for (var i = 0; i < data.length; i++) buf.push(data[i], 8);
    // Terminator (up to 4 bits), byte-align, then pad bytes 0xEC / 0x11.
    var remaining = capacity * 8 - buf.bits.length;
    buf.push(0, Math.min(4, remaining));
    if (buf.bits.length % 8 !== 0) buf.push(0, 8 - (buf.bits.length % 8));
    var padToggle = true;
    while (buf.bits.length < capacity * 8) {
      buf.push(padToggle ? 0xEC : 0x11, 8);
      padToggle = !padToggle;
    }
    var dataCodewords = buf.toBytes();

    // Split into RS blocks, compute ECC, interleave.
    var spec = M_BLOCKS[version];
    var blocks = [];
    var offset = 0;
    for (var g = 0; g < spec.blocks.length; g++) {
      var count = spec.blocks[g][0];
      var len = spec.blocks[g][1];
      for (var b = 0; b < count; b++) {
        var chunk = dataCodewords.slice(offset, offset + len);
        offset += len;
        blocks.push({ data: chunk, ecc: rsComputeEcc(chunk, spec.ec) });
      }
    }
    var interleaved = [];
    var maxData = 0;
    for (var m = 0; m < blocks.length; m++) maxData = Math.max(maxData, blocks[m].data.length);
    for (var col = 0; col < maxData; col++) {
      for (var bi = 0; bi < blocks.length; bi++) {
        if (col < blocks[bi].data.length) interleaved.push(blocks[bi].data[col]);
      }
    }
    for (var ec = 0; ec < spec.ec; ec++) {
      for (var bj = 0; bj < blocks.length; bj++) interleaved.push(blocks[bj].ecc[ec]);
    }
    return { version: version, codewords: interleaved };
  }

  // ---- Matrix construction --------------------------------------------------
  function makeMatrix(version) {
    var size = version * 4 + 17;
    return {
      size: size,
      modules: new Uint8Array(size * size),   // 0/1 dark
      reserved: new Uint8Array(size * size)   // 1 = function module
    };
  }
  function setModule(m, x, y, dark, reserve) {
    var idx = y * m.size + x;
    m.modules[idx] = dark ? 1 : 0;
    if (reserve) m.reserved[idx] = 1;
  }
  function isReserved(m, x, y) { return m.reserved[y * m.size + x] === 1; }

  function drawFinder(m, cx, cy) {
    for (var dy = -4; dy <= 4; dy++) {
      for (var dx = -4; dx <= 4; dx++) {
        var x = cx + dx, y = cy + dy;
        if (x < 0 || y < 0 || x >= m.size || y >= m.size) continue;
        var dist = Math.max(Math.abs(dx), Math.abs(dy));
        setModule(m, x, y, dist !== 2 && dist !== 4, true);
      }
    }
  }
  function drawAlignment(m, cx, cy) {
    for (var dy = -2; dy <= 2; dy++) {
      for (var dx = -2; dx <= 2; dx++) {
        var dist = Math.max(Math.abs(dx), Math.abs(dy));
        setModule(m, cx + dx, cy + dy, dist !== 1, true);
      }
    }
  }
  function drawFunctionPatterns(m, version) {
    var size = m.size;
    drawFinder(m, 3, 3);
    drawFinder(m, size - 4, 3);
    drawFinder(m, 3, size - 4);
    // Timing
    for (var i = 8; i < size - 8; i++) {
      if (!isReserved(m, i, 6)) setModule(m, i, 6, i % 2 === 0, true);
      if (!isReserved(m, 6, i)) setModule(m, 6, i, i % 2 === 0, true);
    }
    // Alignment (skip any overlapping a finder)
    var centers = ALIGNMENT[version];
    for (var a = 0; a < centers.length; a++) {
      for (var b = 0; b < centers.length; b++) {
        var cx = centers[a], cy = centers[b];
        var nearFinder =
          (cx <= 8 && cy <= 8) ||
          (cx <= 8 && cy >= size - 9) ||
          (cx >= size - 9 && cy <= 8);
        if (!nearFinder) drawAlignment(m, cx, cy);
      }
    }
    // Reserve format info areas (filled later)
    for (var f = 0; f < 9; f++) {
      if (!isReserved(m, f, 8)) setModule(m, f, 8, 0, true);
      if (!isReserved(m, 8, f)) setModule(m, 8, f, 0, true);
    }
    for (var g = 0; g < 8; g++) {
      if (!isReserved(m, size - 1 - g, 8)) setModule(m, size - 1 - g, 8, 0, true);
      if (!isReserved(m, 8, size - 1 - g)) setModule(m, 8, size - 1 - g, 0, true);
    }
    // Dark module
    setModule(m, 8, size - 8, 1, true);
    // Version info (v >= 7): two 3x6 blocks
    if (version >= 7) {
      var bits = versionInfoBits(version);
      for (var v = 0; v < 18; v++) {
        var bit = (bits >>> v) & 1;
        var row = Math.floor(v / 3);
        var col = v % 3;
        setModule(m, row, size - 11 + col, bit, true);
        setModule(m, size - 11 + col, row, bit, true);
      }
    }
  }
  function versionInfoBits(version) {
    // BCH(18,6), generator 0x1F25
    var rem = version;
    for (var i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1F25);
    return (version << 12) | rem;
  }
  function formatInfoBits(maskId) {
    // ECC M = 00; 5 data bits = ecc(2) + mask(3); BCH(15,5) gen 0x537; XOR 0x5412
    var data = (0 << 3) | maskId;
    var rem = data;
    for (var i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    return (((data << 10) | rem) ^ 0x5412) & 0x7FFF;
  }
  function drawFormatInfo(m, maskId) {
    var bits = formatInfoBits(maskId);
    var size = m.size;
    // First copy around the top-left finder (explicit placement per spec,
    // index 0 = LSB).
    var place = function (i, x, y) { setModule(m, x, y, (bits >>> i) & 1, true); };
    place(0, 8, 0); place(1, 8, 1); place(2, 8, 2); place(3, 8, 3);
    place(4, 8, 4); place(5, 8, 5); place(6, 8, 7); place(7, 8, 8);
    place(8, 7, 8); place(9, 5, 8); place(10, 4, 8); place(11, 3, 8);
    place(12, 2, 8); place(13, 1, 8); place(14, 0, 8);
    // Second copy: bits 0-7 along the top-right row (x = size-1 … size-8),
    // bits 8-14 down the bottom-left column (y = size-7 … size-1).
    for (var j = 0; j < 8; j++) setModule(m, size - 1 - j, 8, (bits >>> j) & 1, true);
    for (var k = 8; k < 15; k++) setModule(m, 8, size - 15 + k, (bits >>> k) & 1, true);
  }

  function maskBit(maskId, x, y) {
    switch (maskId) {
      case 0: return (x + y) % 2 === 0;
      case 1: return y % 2 === 0;
      case 2: return x % 3 === 0;
      case 3: return (x + y) % 3 === 0;
      case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
      case 5: return ((x * y) % 2 + (x * y) % 3) === 0;
      case 6: return (((x * y) % 2 + (x * y) % 3) % 2) === 0;
      default: return (((x + y) % 2 + (x * y) % 3) % 2) === 0;
    }
  }

  function placeData(m, codewords) {
    var size = m.size;
    var bitIndex = 0;
    var totalBits = codewords.length * 8;
    var upward = true;
    for (var right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5; // skip timing column
      for (var vert = 0; vert < size; vert++) {
        var y = upward ? size - 1 - vert : vert;
        for (var h = 0; h < 2; h++) {
          var x = right - h;
          if (isReserved(m, x, y)) continue;
          var bit = 0;
          if (bitIndex < totalBits) {
            bit = (codewords[bitIndex >>> 3] >>> (7 - (bitIndex & 7))) & 1;
            bitIndex++;
          }
          setModule(m, x, y, bit, false);
        }
      }
      upward = !upward;
    }
  }

  function applyMask(m, maskId) {
    for (var y = 0; y < m.size; y++) {
      for (var x = 0; x < m.size; x++) {
        if (!isReserved(m, x, y) && maskBit(maskId, x, y)) {
          m.modules[y * m.size + x] ^= 1;
        }
      }
    }
  }

  function penaltyScore(m) {
    var size = m.size;
    var score = 0;
    var get = function (x, y) { return m.modules[y * m.size + x]; };
    // Rule 1: runs of >= 5
    for (var y = 0; y < size; y++) {
      var runColor = get(0, y), runLen = 1;
      for (var x = 1; x < size; x++) {
        if (get(x, y) === runColor) { runLen++; }
        else { if (runLen >= 5) score += 3 + (runLen - 5); runColor = get(x, y); runLen = 1; }
      }
      if (runLen >= 5) score += 3 + (runLen - 5);
    }
    for (var x2 = 0; x2 < size; x2++) {
      var runColor2 = get(x2, 0), runLen2 = 1;
      for (var y2 = 1; y2 < size; y2++) {
        if (get(x2, y2) === runColor2) { runLen2++; }
        else { if (runLen2 >= 5) score += 3 + (runLen2 - 5); runColor2 = get(x2, y2); runLen2 = 1; }
      }
      if (runLen2 >= 5) score += 3 + (runLen2 - 5);
    }
    // Rule 2: 2x2 blocks
    for (var yb = 0; yb < size - 1; yb++) {
      for (var xb = 0; xb < size - 1; xb++) {
        var c = get(xb, yb);
        if (c === get(xb + 1, yb) && c === get(xb, yb + 1) && c === get(xb + 1, yb + 1)) score += 3;
      }
    }
    // Rule 3: finder-like 1:1:3:1:1 with 4 light modules on a side
    var patA = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
    var patB = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
    var matches = function (getter, fixed, i) {
      var okA = true, okB = true;
      for (var k = 0; k < 11; k++) {
        var val = getter(fixed, i + k);
        if (val !== patA[k]) okA = false;
        if (val !== patB[k]) okB = false;
      }
      return okA || okB;
    };
    var rowGet = function (y3, x3) { return get(x3, y3); };
    var colGet = function (x3, y3) { return get(x3, y3); };
    for (var y4 = 0; y4 < size; y4++) {
      for (var i4 = 0; i4 <= size - 11; i4++) if (matches(rowGet, y4, i4)) score += 40;
    }
    for (var x4 = 0; x4 < size; x4++) {
      for (var i5 = 0; i5 <= size - 11; i5++) if (matches(colGet, x4, i5)) score += 40;
    }
    // Rule 4: dark proportion
    var dark = 0;
    for (var d = 0; d < m.modules.length; d++) dark += m.modules[d];
    var percent = (dark * 100) / m.modules.length;
    score += Math.floor(Math.abs(percent - 50) / 5) * 10;
    return score;
  }

  function encodeMatrix(text) {
    var built = buildCodewords(text);
    var best = null;
    var bestScore = Infinity;
    for (var maskId = 0; maskId < 8; maskId++) {
      var m = makeMatrix(built.version);
      drawFunctionPatterns(m, built.version);
      drawFormatInfo(m, maskId);
      placeData(m, built.codewords);
      applyMask(m, maskId);
      var score = penaltyScore(m);
      if (score < bestScore) { bestScore = score; best = m; }
    }
    return {
      size: best.size,
      modules: best.modules,
      get: function (x, y) { return best.modules[y * best.size + x]; }
    };
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** Render as crispEdges SVG (same visual language as the upgrade insert). */
  function svg(text, opts) {
    var o = opts || {};
    var dark = o.dark || '#0F356F';
    var light = o.light || '#FFFFFF';
    var quiet = 4;
    var m = encodeMatrix(text);
    var dim = m.size + quiet * 2;
    // Row-run compression: one h-run rect path segment per horizontal run.
    var segs = [];
    for (var y = 0; y < m.size; y++) {
      var x = 0;
      while (x < m.size) {
        if (m.get(x, y)) {
          var start = x;
          while (x < m.size && m.get(x, y)) x++;
          segs.push('M' + start + ' ' + y + 'h' + (x - start) + 'v1h-' + (x - start) + 'z');
        } else { x++; }
      }
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + dim + ' ' + dim + '" shape-rendering="crispEdges" role="img" aria-label="' + escapeAttr(o.ariaLabel || 'QR code') + '">' +
      '<rect width="' + dim + '" height="' + dim + '" fill="' + escapeAttr(light) + '"/>' +
      '<path transform="translate(' + quiet + ' ' + quiet + ')" d="' + segs.join('') + '" fill="' + escapeAttr(dark) + '"/>' +
      '</svg>';
  }

  var api = { matrix: encodeMatrix, svg: svg };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.McDuffyBoxQR = api;
})();
